import React, { FC, useMemo, useState } from "react";
import { Control, useFieldArray } from "react-hook-form";
import {
  CompaniesBasicsListItemDto,
  UserRole,
  UsersListItemDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { Checkbox, Switch } from "@mui/material";
import { isEmpty } from "lodash";

import { CompanyAssignmentFieldItem, FormValues } from "../../UserEditor";

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dropdown,
  Typography,
} from "@components/base";
import { Chip } from "@components/common";
import { useGetCompaniesBasics } from "@api";
import { useUserStore } from "@store/user";
import { USER_ROLE_VALUES } from "@type/User";

interface CompanyAssignmentTable {
  user?: UsersListItemDto;
  control: Control<FormValues, unknown>;
  disabled?: boolean;
}

const CompanyAssignmentTable: FC<CompanyAssignmentTable> = ({
  user,
  control,
  disabled,
}) => {
  const { t } = useTranslation();
  const { user: currentUser, jwtTokenData } = useUserStore();
  const { companiesBasics } = useGetCompaniesBasics(currentUser);

  const currentUserRole = jwtTokenData?.role;
  const canEditRoles =
    currentUserRole === UserRole.Admin || currentUserRole === UserRole.Owner;

  const companyItems = (companiesBasics?.items ??
    []) as CompaniesBasicsListItemDto[];

  const { fields: userCompanyAssignments, update: updateUserCompany } =
    useFieldArray({
      name: "companies",
      control,
    });

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);

  const isSelected = (companyId: string): boolean =>
    selectedCompanyIds.includes(companyId);

  const toggleRowSelection = (companyId: string): void => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId],
    );
  };

  const isRowDisabled = (companyId: string): boolean => {
    if (!user || !currentUser || !companiesBasics) return false;

    const company = companiesBasics.items.find(
      (company) => company.id === companyId,
    );

    if (!company) return false;

    return user.id === currentUser.id && company.id === currentUser.activeCompanyId;
  };
  const visibleCompanies = useMemo(() => {
    if (!companyItems.length || !currentUser?.activeCompanyId) {
      return companyItems;
    }

    const activeCompany = companyItems.find(
      (c) => c.id === currentUser.activeCompanyId,
    );

    if (!activeCompany) {
      return companyItems;
    }

    if (activeCompany.companyType === 0) {
      return companyItems;
    }

    const children = companyItems.filter(
      (c) => c.parentCompanyId === activeCompany.id,
    );

    return [activeCompany, ...children];
  }, [companyItems, currentUser?.activeCompanyId]);
  const allSelectableIds = useMemo(() => {
    if (!visibleCompanies.length) return [];

    return visibleCompanies
      .filter((company) => !isRowDisabled(company.id))
      .map((company) => company.id);
  }, [visibleCompanies, user, currentUser]);

  const allSelected = useMemo(() => {
    return (
      !isEmpty(allSelectableIds) &&
      allSelectableIds.every((id) => selectedCompanyIds.includes(id))
    );
  }, [allSelectableIds, selectedCompanyIds]);

  const someSelected = useMemo(() => {
    return (
      !isEmpty(allSelectableIds) &&
      allSelectableIds.some((id) => selectedCompanyIds.includes(id)) &&
      !allSelected
    );
  }, [allSelectableIds, selectedCompanyIds, allSelected]);

  const toggleAllSelection = (checked: boolean): void => {
    if (checked) {
      setSelectedCompanyIds(allSelectableIds);
    } else {
      setSelectedCompanyIds([]);
    }
  };

  const findAssignmentIndex = (companyId: string): number =>
    userCompanyAssignments.findIndex((item) => item.companyId === companyId);

  const applyChange = (
    targetCompanyId: string,
    changer: (item: CompanyAssignmentFieldItem) => CompanyAssignmentFieldItem,
  ): void => {
    const hasSelection = !isEmpty(selectedCompanyIds);

    if (!hasSelection) {
      const targetIndex = findAssignmentIndex(targetCompanyId);

      if (targetIndex === -1) return;

      const current = userCompanyAssignments[targetIndex];

      updateUserCompany(targetIndex, changer(current));

      return;
    }

    userCompanyAssignments.forEach((field, index) => {
      const companyId = field.companyId;

      if (!selectedCompanyIds.includes(companyId)) return;
      if (isRowDisabled(companyId)) return;

      updateUserCompany(index, changer(field));
    });
  };

  const handleRoleChange = (companyId: string, role: UserRole): void => {
    applyChange(companyId, (item) => ({
      ...item,
      role,
    }));
  };

  const toggleCompanyAssignment = (
    companyId: string,
    companyAssignment: CompanyAssignmentFieldItem,
  ): void => {
    const isAssigned = !companyAssignment.isAssigned;

    applyChange(companyId, (item) => ({
      ...item,
      isAssigned: isAssigned,
      role: isAssigned ? (item.role ?? UserRole.Viewer) : UserRole.Viewer,
    }));
  };

  return (
    <TableContainer>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell width={70} align="center">
              <Checkbox
                disabled={disabled}
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(_, checked) => toggleAllSelection(checked)}
              />
            </TableCell>
            <TableCell width={100}>
              {t("usersManagement.userEditor.assignmentTable.assign")}
            </TableCell>
            <TableCell>
              {t("usersManagement.userEditor.assignmentTable.entity")}
            </TableCell>
            <TableCell width={200}>
              {t("usersManagement.userEditor.assignmentTable.role")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleCompanies.map((company) => {
            const assignmentIndex = findAssignmentIndex(company.id);

            if (assignmentIndex === -1) return null;

            const companyAssignment = userCompanyAssignments[assignmentIndex];

            const disableCurrentRow = isRowDisabled(company.id);
            const hasSelection = !isEmpty(selectedCompanyIds);
            const isEditable =
              !disableCurrentRow && (!hasSelection || isSelected(company.id));

            return (
              <TableRow key={company.id}>
                <TableCell>
                  <Checkbox
                    disabled={disabled || disableCurrentRow}
                    checked={isSelected(company.id)}
                    onChange={() => toggleRowSelection(company.id)}
                  />
                </TableCell>

                <TableCell>
                  <Switch
                    checked={companyAssignment.isAssigned}
                    disabled={disabled || !isEditable}
                    onChange={() => {
                      if (isEditable) {
                        toggleCompanyAssignment(company.id, companyAssignment);
                      }
                    }}
                  />
                </TableCell>

                <TableCell>{company.name}</TableCell>

                <TableCell>
                  {companyAssignment.isAssigned &&
                    companyAssignment.role !== null &&
                    canEditRoles && (
                      <Dropdown
                        disabled={!isEditable && hasSelection}
                        button={{
                          title: (
                            <Chip
                              label={t(
                                `roles.${UserRole[companyAssignment.role].toLowerCase()}`,
                              )}
                              type={companyAssignment.role as UserRole}
                              size="small"
                            />
                          ),
                          variant: "text",
                          size: "small",
                        }}
                        options={Object.entries(USER_ROLE_VALUES)
                          .filter(
                            ([id]) =>
                              currentUserRole === UserRole.Admin ||
                              Number(id) !== UserRole.Admin,
                          )
                          .map(([id, key]) => ({
                            id: Number(id),
                            title: (
                              <Chip
                                label={t(`roles.${key}`)}
                                type={Number(id)}
                                size="small"
                              />
                            ),
                          }))}
                        onSelect={(option) => {
                          handleRoleChange(company.id, option.id as UserRole);
                        }}
                      />
                    )}
                  {companyAssignment.isAssigned &&
                    companyAssignment.role !== null &&
                    !canEditRoles && (
                      <Chip
                        label={t(
                          `roles.${UserRole[companyAssignment.role].toLowerCase()}`,
                        )}
                        type={companyAssignment.role as UserRole}
                        size="small"
                      />
                    )}
                  {companyAssignment.isAssigned &&
                    companyAssignment.role === null && (
                      <Typography variant="caption" color="text.secondary">
                        {t("usersManagement.userEditor.assignmentTable.selectRole")}
                      </Typography>
                    )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CompanyAssignmentTable;
